import { validate as isUuid } from "uuid"
import type { MediaAccess } from "../interface/media"

export interface MediaAccessState {
  phase: "idle" | "loading" | "ready" | "error"
  url?: string
  expiresAt?: string
  error?: string
}
interface Entry {
  state: MediaAccessState
  listeners: Set<() => void>
  request?: AbortController
  timer?: ReturnType<typeof setTimeout>
  resourceRetried: boolean
}
const idle: MediaAccessState = { phase: "idle" }
const refreshAhead = 60_000
const failure = (error: unknown): MediaAccessState => {
  const status = (error as { status?: number } | undefined)?.status
  return {
    phase: "error",
    error:
      status === 404
        ? "Файл удалён"
        : status === 409
          ? "Файл удаляется"
          : status === 401 || status === 403
            ? "Нет доступа к файлу"
            : "Не удалось загрузить предпросмотр",
  }
}

/** Private capability URLs live only here and in render descriptors. Neither
 * responses nor errors/Request metadata are dispatched or persisted. */
export function createMediaAccessOwner(
  load: (id: string, signal: AbortSignal) => Promise<MediaAccess>,
) {
  const entries = new Map<string, Entry>()
  let visible = true
  let version = 0
  const update = (entry: Entry, state: MediaAccessState) => {
    entry.state = state
    version++
    entry.listeners.forEach((notify) => notify())
  }
  const entryFor = (id: string) => {
    let entry = entries.get(id)
    if (!entry) {
      entry = { state: idle, listeners: new Set(), resourceRetried: false }
      entries.set(id, entry)
    }
    return entry
  }
  const fresh = (entry: Entry) =>
    entry.state.phase === "ready" &&
    Date.parse(entry.state.expiresAt!) - Date.now() > refreshAhead
  const schedule = (id: string, entry: Entry) => {
    clearTimeout(entry.timer)
    if (entry.state.phase !== "ready") return
    const subscribed = visible && entry.listeners.size > 0
    const delay =
      Date.parse(entry.state.expiresAt!) -
      Date.now() -
      (subscribed ? refreshAhead : 0)
    entry.timer = setTimeout(
      () => {
        if (entries.get(id) !== entry) return
        if (visible && entry.listeners.size) void request(id, entry)
        else if (!entry.listeners.size) entries.delete(id)
        else update(entry, idle)
      },
      Math.max(0, delay),
    )
  }
  const request = async (id: string, entry: Entry) => {
    if (!isUuid(id) || entry.request || entries.get(id) !== entry) return
    clearTimeout(entry.timer)
    const controller = new AbortController()
    entry.request = controller
    update(entry, { phase: "loading" })
    try {
      const data = await load(id, controller.signal)
      if (entries.get(id) !== entry || controller.signal.aborted) return
      const url = new URL(data.url)
      if (
        !/^https?:$/.test(url.protocol) ||
        url.username ||
        url.password ||
        !Number.isFinite(Date.parse(data.expiresAt)) ||
        Date.parse(data.expiresAt) - Date.now() <= refreshAhead
      )
        throw new Error("Invalid media access")
      update(entry, {
        phase: "ready",
        url: data.url,
        expiresAt: data.expiresAt,
      })
      schedule(id, entry)
    } catch (error) {
      if (entries.get(id) === entry && !controller.signal.aborted)
        update(entry, failure(error))
    } finally {
      if (entry.request === controller) entry.request = undefined
    }
  }
  return {
    getVersion: () => version,
    getSnapshot: (id: string) => entries.get(id)?.state ?? idle,
    subscribe(id: string, notify: () => void) {
      if (!isUuid(id)) return () => {}
      const entry = entryFor(id)
      entry.listeners.add(notify)
      if (visible && !fresh(entry) && entry.state.phase !== "error")
        void request(id, entry)
      else schedule(id, entry)
      return () => {
        entry.listeners.delete(notify)
        if (entries.get(id) === entry) {
          schedule(id, entry)
        }
      }
    },
    retryFor(id: string) {
      const entry = entries.get(id)
      return () => {
        if (!entry || entries.get(id) !== entry) return
        entry.resourceRetried = false
        void request(id, entry)
      }
    },
    resourceError(id: string, url: string) {
      const entry = entries.get(id)
      if (!entry || entry.state.phase !== "ready" || entry.state.url !== url)
        return
      if (entry.resourceRetried) {
        clearTimeout(entry.timer)
        update(entry, failure(undefined))
      } else {
        entry.resourceRetried = true
        void request(id, entry)
      }
    },
    setVisible(next: boolean) {
      visible = next
      entries.forEach((entry, id) => {
        if (
          visible &&
          entry.listeners.size &&
          !fresh(entry) &&
          entry.state.phase !== "error"
        )
          void request(id, entry)
        else schedule(id, entry)
      })
    },
    clear() {
      const previous = [...entries.values()]
      entries.clear()
      previous.forEach((entry) => {
        entry.request?.abort()
        clearTimeout(entry.timer)
        update(entry, idle)
      })
    },
  }
}
